import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';

// Mapbox token - get from environment or use public demo token
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1IjoibWFwYm94IiwiYSI6ImNpejY4NXVycTA2emYycXBndHRqcmZ3N3gifQ.rJcFIG214AriISLbB6B5aw';

// Default US center and zoom
const US_CENTER = [-96, 38];
const US_ZOOM = 3.5;
const US_BOUNDS = [
  [-125, 24], // Southwest
  [-66, 50]   // Northeast
];

// Major US cities for map labels
const MAJOR_CITIES = [
  { name: 'New York', coordinates: [-74.006, 40.7128] },
  { name: 'Los Angeles', coordinates: [-118.2437, 34.0522] },
  { name: 'Chicago', coordinates: [-87.6298, 41.8781] },
  { name: 'Houston', coordinates: [-95.3698, 29.7604] },
  { name: 'Phoenix', coordinates: [-112.0740, 33.4484] },
  { name: 'Philadelphia', coordinates: [-75.1652, 39.9526] },
  { name: 'San Antonio', coordinates: [-98.4936, 29.4241] },
  { name: 'San Diego', coordinates: [-117.1611, 32.7157] },
  { name: 'Dallas', coordinates: [-96.7970, 32.7767] },
  { name: 'San Francisco', coordinates: [-122.4194, 37.7749] },
  { name: 'Austin', coordinates: [-97.7431, 30.2672] },
  { name: 'Seattle', coordinates: [-122.3321, 47.6062] },
  { name: 'Denver', coordinates: [-104.9903, 39.7392] },
  { name: 'Boston', coordinates: [-71.0589, 42.3601] },
  { name: 'Atlanta', coordinates: [-84.3880, 33.7490] },
  { name: 'Miami', coordinates: [-80.1918, 25.7617] },
  { name: 'Washington DC', coordinates: [-77.0369, 38.9072] },
  { name: 'Detroit', coordinates: [-83.0458, 42.3314] },
  { name: 'Minneapolis', coordinates: [-93.2650, 44.9778] },
  { name: 'Portland', coordinates: [-122.6765, 45.5152] },
  { name: 'Las Vegas', coordinates: [-115.1398, 36.1699] },
  { name: 'Charlotte', coordinates: [-80.8431, 35.2271] },
  { name: 'Nashville', coordinates: [-86.7816, 36.1627] },
  { name: 'Orlando', coordinates: [-81.3792, 28.5383] },
  { name: 'Tampa', coordinates: [-82.4572, 27.9506] },
  { name: 'St. Louis', coordinates: [-90.1994, 38.6270] },
  { name: 'Baltimore', coordinates: [-76.6122, 39.2904] },
  { name: 'Cleveland', coordinates: [-81.6944, 41.4993] },
  { name: 'Pittsburgh', coordinates: [-79.9959, 40.4406] },
  { name: 'Cincinnati', coordinates: [-84.5120, 39.1031] },
  { name: 'Kansas City', coordinates: [-94.5786, 39.0997] },
  { name: 'New Orleans', coordinates: [-90.0715, 29.9511] },
  { name: 'Salt Lake City', coordinates: [-111.8910, 40.7608] },
  { name: 'Sacramento', coordinates: [-121.4944, 38.5816] },
  { name: 'Raleigh', coordinates: [-78.6382, 35.7796] },
];

// State label positions (centroids with abbreviations)
const STATE_LABELS = [
  { abbr: 'WA', coordinates: [-120.5, 47.4] },
  { abbr: 'OR', coordinates: [-120.5, 43.9] },
  { abbr: 'CA', coordinates: [-119.4, 37.2] },
  { abbr: 'NV', coordinates: [-116.9, 39.0] },
  { abbr: 'ID', coordinates: [-114.7, 44.2] },
  { abbr: 'MT', coordinates: [-109.6, 47.0] },
  { abbr: 'WY', coordinates: [-107.5, 43.0] },
  { abbr: 'UT', coordinates: [-111.5, 39.3] },
  { abbr: 'AZ', coordinates: [-111.9, 34.2] },
  { abbr: 'CO', coordinates: [-105.5, 39.0] },
  { abbr: 'NM', coordinates: [-106.0, 34.5] },
  { abbr: 'ND', coordinates: [-100.5, 47.5] },
  { abbr: 'SD', coordinates: [-100.2, 44.4] },
  { abbr: 'NE', coordinates: [-99.8, 41.5] },
  { abbr: 'KS', coordinates: [-98.4, 38.5] },
  { abbr: 'OK', coordinates: [-97.5, 35.5] },
  { abbr: 'TX', coordinates: [-99.9, 31.5] },
  { abbr: 'MN', coordinates: [-94.6, 46.3] },
  { abbr: 'IA', coordinates: [-93.5, 42.0] },
  { abbr: 'MO', coordinates: [-92.5, 38.5] },
  { abbr: 'AR', coordinates: [-92.4, 34.8] },
  { abbr: 'LA', coordinates: [-91.9, 31.0] },
  { abbr: 'WI', coordinates: [-89.6, 44.6] },
  { abbr: 'IL', coordinates: [-89.3, 40.0] },
  { abbr: 'MI', coordinates: [-85.6, 44.3] },
  { abbr: 'IN', coordinates: [-86.1, 40.0] },
  { abbr: 'OH', coordinates: [-82.8, 40.4] },
  { abbr: 'KY', coordinates: [-85.8, 37.8] },
  { abbr: 'TN', coordinates: [-86.3, 35.8] },
  { abbr: 'MS', coordinates: [-89.7, 32.7] },
  { abbr: 'AL', coordinates: [-86.8, 32.8] },
  { abbr: 'GA', coordinates: [-83.4, 32.6] },
  { abbr: 'FL', coordinates: [-81.7, 28.1] },
  { abbr: 'SC', coordinates: [-80.9, 33.9] },
  { abbr: 'NC', coordinates: [-79.8, 35.5] },
  { abbr: 'VA', coordinates: [-78.8, 37.5] },
  { abbr: 'WV', coordinates: [-80.6, 38.9] },
  { abbr: 'PA', coordinates: [-77.5, 41.0] },
  { abbr: 'NY', coordinates: [-75.5, 43.0] },
  { abbr: 'VT', coordinates: [-72.7, 44.0] },
  { abbr: 'NH', coordinates: [-71.5, 43.7] },
  { abbr: 'ME', coordinates: [-69.2, 45.3] },
  { abbr: 'MA', coordinates: [-71.5, 42.2] },
  { abbr: 'RI', coordinates: [-71.5, 41.6] },
  { abbr: 'CT', coordinates: [-72.7, 41.6] },
  { abbr: 'NJ', coordinates: [-74.4, 40.1] },
  { abbr: 'DE', coordinates: [-75.5, 39.0] },
  { abbr: 'MD', coordinates: [-76.6, 39.0] },
  { abbr: 'DC', coordinates: [-77.0, 38.9] },
];

// Metrics where lower is better
const LOWER_IS_BETTER = [
  'residential_cents_kwh',
  'commercial_cents_kwh',
  'industrial_cents_kwh',
  'saidi_without_med',
  'saidi_with_med',
  'saifi_without_med',
  'saifi_with_med',
  'caidi',
  'co2_per_customer'
];

// Get category from metric name
function getMetricCategory(metric) {
  if (metric.includes('cents_kwh') || metric.includes('rate') || metric.includes('change')) {
    return 'rates';
  }
  if (metric.includes('saidi') || metric.includes('saifi') || metric.includes('caidi')) {
    return 'reliability';
  }
  if (metric.includes('ami') || metric.includes('meter')) {
    return 'infrastructure';
  }
  return 'environmental';
}

// Get color based on comparison to national average
function getColorForValue(value, nationalAvg, metric) {
  if (value === null || value === undefined || nationalAvg === null || nationalAvg === undefined) {
    return '#d1d5db'; // Gray for no data
  }

  const lowerIsBetter = LOWER_IS_BETTER.includes(metric);
  const percentDiff = ((value - nationalAvg) / nationalAvg) * 100;

  if (lowerIsBetter) {
    // For rates, SAIDI, CO2: lower is better (negative diff = good)
    if (percentDiff < -15) return '#10b981'; // Much better (dark green)
    if (percentDiff < -5) return '#34d399';  // Better (light green)
    if (percentDiff < 5) return '#fbbf24';   // Average (yellow)
    if (percentDiff < 15) return '#fb923c';  // Worse (orange)
    return '#ef4444';                         // Much worse (red)
  } else {
    // For renewables, AMI: higher is better (positive diff = good)
    if (percentDiff > 15) return '#10b981';  // Much better
    if (percentDiff > 5) return '#34d399';   // Better
    if (percentDiff > -5) return '#fbbf24';  // Average
    if (percentDiff > -15) return '#fb923c'; // Worse
    return '#ef4444';                         // Much worse
  }
}

const UtilityMap = forwardRef(({
  territoriesData,
  statesData,
  utilitiesData,
  yearDataCache,
  selectedYear,
  selectedMetric,
  selectedUtilityId,
  onUtilitySelect
}, ref) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const popup = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [styleLoaded, setStyleLoaded] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(US_ZOOM);
  const [territoriesLoaded, setTerritoriesLoaded] = useState(false);
  const selectedYearRef = useRef(selectedYear);
  const yearDataCacheRef = useRef(yearDataCache);

  // Keep refs in sync with props for event handlers
  useEffect(() => {
    selectedYearRef.current = selectedYear;
  }, [selectedYear]);

  useEffect(() => {
    yearDataCacheRef.current = yearDataCache;
  }, [yearDataCache]);

  // Zoom to a specific utility's bounds
  const zoomToUtility = useCallback((utilityId) => {
    if (!map.current || !territoriesData) return;

    const feature = territoriesData.features.find(
      f => f.properties.utility_id === utilityId
    );

    if (feature) {
      try {
        const bbox = turf.bbox(feature);
        map.current.fitBounds(bbox, {
          padding: 50,
          maxZoom: 10,
          duration: 1000
        });
      } catch (e) {
        console.warn('Could not zoom to utility:', e);
      }
    }
  }, [territoriesData]);

  // Expose zoomToUtility to parent via ref
  useImperativeHandle(ref, () => ({
    zoomToUtility
  }), [zoomToUtility]);

  // Zoom to fit all utilities
  const zoomToFitAll = useCallback(() => {
    if (!map.current) return;
    map.current.fitBounds(US_BOUNDS, {
      padding: 20,
      duration: 1000
    });
  }, []);

  // Zoom in/out functions
  const zoomIn = useCallback(() => {
    if (map.current) {
      map.current.zoomIn({ duration: 300 });
    }
  }, []);

  const zoomOut = useCallback(() => {
    if (map.current) {
      map.current.zoomOut({ duration: 300 });
    }
  }, []);

  // CRITICAL: Force map to render tiles
  const forceMapRender = useCallback(() => {
    if (!map.current) return;
    console.log('[UtilityMap] Forcing map render...');

    map.current.resize();
    map.current.triggerRepaint();

    // Force a tiny viewport change to trigger tile loading
    const center = map.current.getCenter();
    const zoom = map.current.getZoom();
    map.current.jumpTo({ center, zoom });
  }, []);

  // Initialize map - COMPLETELY REWRITTEN
  useEffect(() => {
    if (map.current) return;
    if (!mapContainer.current) {
      console.error('[UtilityMap] Map container ref is null');
      return;
    }

    // DEBUG: Log container dimensions
    const containerRect = mapContainer.current.getBoundingClientRect();
    console.log('[UtilityMap] 1. Container dimensions:', containerRect.width, 'x', containerRect.height);
    console.log('[UtilityMap] 2. Mapbox token set:', !!mapboxgl.accessToken);
    console.log('[UtilityMap] 3. Initializing map...');

    // Create map with CENTER and ZOOM instead of bounds (more reliable)
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: US_CENTER,
      zoom: US_ZOOM,
      minZoom: 2,
      maxZoom: 12,
      renderWorldCopies: false,
      preserveDrawingBuffer: true // Helps with rendering issues
    });

    popup.current = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      maxWidth: '300px'
    });

    // Handle map errors
    map.current.on('error', (e) => {
      console.error('[UtilityMap] Map error:', e.error);
    });

    // CRITICAL: style.load fires BEFORE load - use this for adding sources/layers
    map.current.on('style.load', () => {
      console.log('[UtilityMap] 4. Style loaded');
      setStyleLoaded(true);
    });

    // load event - map is fully rendered
    map.current.on('load', () => {
      console.log('[UtilityMap] 5. Map fully loaded');
      setMapLoaded(true);

      // Force render with multiple fallbacks
      forceMapRender();
      setTimeout(forceMapRender, 100);
      setTimeout(forceMapRender, 250);
      setTimeout(forceMapRender, 500);
      setTimeout(forceMapRender, 1000);
    });

    // idle event - fires when map is fully rendered and idle
    map.current.on('idle', () => {
      console.log('[UtilityMap] 6. Map idle (fully rendered)');
      map.current.resize();
    });

    // render event - for debugging
    map.current.once('render', () => {
      console.log('[UtilityMap] 7. First render occurred');
    });

    // Track zoom level
    map.current.on('zoom', () => {
      setCurrentZoom(map.current.getZoom());
    });

    // Add navigation control
    map.current.addControl(new mapboxgl.NavigationControl({
      showCompass: true,
      showZoom: true,
      visualizePitch: true
    }), 'top-left');

    // Add fullscreen control
    map.current.addControl(new mapboxgl.FullscreenControl(), 'top-left');

    // Add scale control
    map.current.addControl(new mapboxgl.ScaleControl({
      maxWidth: 100,
      unit: 'imperial'
    }), 'bottom-right');

    // Enable scroll zoom
    map.current.scrollZoom.setWheelZoomRate(1/200);
    map.current.doubleClickZoom.enable();

    // FALLBACK: Immediate resize attempts
    setTimeout(() => {
      if (map.current) {
        console.log('[UtilityMap] Fallback resize at 100ms');
        map.current.resize();
      }
    }, 100);

    setTimeout(() => {
      if (map.current) {
        console.log('[UtilityMap] Fallback resize at 500ms');
        map.current.resize();
        map.current.triggerRepaint();
      }
    }, 500);

    setTimeout(() => {
      if (map.current) {
        console.log('[UtilityMap] Fallback resize at 1000ms');
        map.current.resize();
        map.current.triggerRepaint();
        // Nuclear option: force a tiny viewport change
        const center = map.current.getCenter();
        map.current.flyTo({ center, zoom: map.current.getZoom(), duration: 1 });
      }
    }, 1000);

    // Window load fallback
    const handleWindowLoad = () => {
      if (map.current) {
        console.log('[UtilityMap] Window load - forcing resize');
        map.current.resize();
        map.current.triggerRepaint();
      }
    };
    window.addEventListener('load', handleWindowLoad);

    return () => {
      window.removeEventListener('load', handleWindowLoad);
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [forceMapRender]);

  // Handle container resize with ResizeObserver
  useEffect(() => {
    if (!mapContainer.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (map.current && entry.contentRect.width > 0 && entry.contentRect.height > 0) {
          console.log('[UtilityMap] Container resized to:', entry.contentRect.width, 'x', entry.contentRect.height);
          map.current.resize();
        }
      }
    });

    resizeObserver.observe(mapContainer.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Add/update data layers when style is loaded AND data is available
  useEffect(() => {
    if (!map.current || !styleLoaded) return;

    console.log('[UtilityMap] Adding data layers, styleLoaded:', styleLoaded, 'statesData:', !!statesData, 'territoriesData:', !!territoriesData);

    // Add states layer (borders)
    if (statesData && !map.current.getSource('states')) {
      console.log('[UtilityMap] Adding states source and layers');

      map.current.addSource('states', {
        type: 'geojson',
        data: statesData
      });

      map.current.addLayer({
        id: 'state-borders',
        type: 'line',
        source: 'states',
        paint: {
          'line-color': '#374151',
          'line-width': 1,
          'line-opacity': 0.5
        }
      });

      // Add state abbreviation labels
      map.current.addSource('state-labels', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: STATE_LABELS.map(state => ({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: state.coordinates },
            properties: { abbr: state.abbr }
          }))
        }
      });

      map.current.addLayer({
        id: 'state-labels',
        type: 'symbol',
        source: 'state-labels',
        layout: {
          'text-field': ['get', 'abbr'],
          'text-size': 11,
          'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'],
          'text-allow-overlap': false
        },
        paint: {
          'text-color': '#6b7280',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.5
        }
      });

      // Add city labels
      map.current.addSource('city-labels', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: MAJOR_CITIES.map(city => ({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: city.coordinates },
            properties: { name: city.name }
          }))
        }
      });

      map.current.addLayer({
        id: 'city-labels',
        type: 'symbol',
        source: 'city-labels',
        layout: {
          'text-field': ['get', 'name'],
          'text-size': 10,
          'text-font': ['DIN Pro Regular', 'Arial Unicode MS Regular'],
          'text-anchor': 'top',
          'text-offset': [0, 0.5],
          'text-allow-overlap': false
        },
        paint: {
          'text-color': '#374151',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1
        },
        minzoom: 5
      });

      // Add city dots
      map.current.addLayer({
        id: 'city-dots',
        type: 'circle',
        source: 'city-labels',
        paint: {
          'circle-radius': 3,
          'circle-color': '#374151',
          'circle-stroke-width': 1,
          'circle-stroke-color': '#ffffff'
        },
        minzoom: 5
      });

      // Force repaint after adding layers
      map.current.triggerRepaint();
    }

    // Add territories layer
    if (territoriesData && !map.current.getSource('territories')) {
      console.log('[UtilityMap] Adding territories source and layers');

      map.current.addSource('territories', {
        type: 'geojson',
        data: territoriesData
      });

      // Fill layer
      map.current.addLayer({
        id: 'utility-fills',
        type: 'fill',
        source: 'territories',
        paint: {
          'fill-color': '#d1d5db',
          'fill-opacity': 0.7
        }
      }, 'state-borders');

      // Border layer for territories
      map.current.addLayer({
        id: 'utility-borders',
        type: 'line',
        source: 'territories',
        paint: {
          'line-color': '#9ca3af',
          'line-width': 0.5
        }
      }, 'state-borders');

      // Highlight layer for selected
      map.current.addLayer({
        id: 'utility-highlight',
        type: 'line',
        source: 'territories',
        paint: {
          'line-color': '#2563eb',
          'line-width': 3
        },
        filter: ['==', 'utility_id', '']
      }, 'state-borders');

      // Mark territories as loaded
      setTerritoriesLoaded(true);
      console.log('[UtilityMap] Territories loaded');

      // Force repaint after adding layers
      map.current.triggerRepaint();

      // Mouse events
      map.current.on('mousemove', 'utility-fills', (e) => {
        if (e.features.length > 0) {
          map.current.getCanvas().style.cursor = 'pointer';

          const feature = e.features[0];
          const utilityId = feature.properties.utility_id;

          // Get utility data - prefer display_name over GeoJSON utility_name
          const utility = utilitiesData?.utilities?.[utilityId];
          const displayName = utility?.display_name || utility?.name || feature.properties.utility_name;
          const parentCompany = utility?.parent_company;
          // Get year data directly from cache (more reliable than utilitiesData.years which may not be loaded)
          const currentYear = selectedYearRef.current;
          const yearData = yearDataCacheRef.current?.[currentYear]?.[utilityId];

          let popupContent = `
            <div class="p-3">
              <h3 class="font-bold text-gray-900 mb-1">${displayName}</h3>
              ${parentCompany ? `<p class="text-xs text-gray-500 mb-2">${parentCompany}</p>` : ''}
          `;

          if (yearData) {
            const rate = yearData.rates?.residential_cents_kwh;
            const customers = yearData.rates?.total_customers;
            const saidi = yearData.reliability?.saidi_without_med;
            const renewable = yearData.environmental?.renewable_pct;
            const ami = yearData.infrastructure?.ami_deployment_pct;

            popupContent += `
              <div class="space-y-1 text-sm">
                ${customers ? `<div>&#x1F465; ${customers.toLocaleString()} customers</div>` : ''}
                ${rate ? `<div>&#x1F4B0; Rate: ${rate.toFixed(1)}&#162;/kWh</div>` : ''}
                ${saidi ? `<div>&#x26A1; SAIDI: ${saidi.toFixed(0)} min</div>` : ''}
                ${renewable ? `<div>&#x1F33F; Renewable: ${renewable.toFixed(0)}%</div>` : ''}
                ${ami ? `<div>&#x1F4CA; AMI: ${ami.toFixed(0)}%</div>` : ''}
              </div>
            `;
          } else {
            popupContent += `<div class="text-sm text-gray-500">No data for ${selectedYearRef.current}</div>`;
          }

          popupContent += `
              <p class="text-xs text-gray-400 mt-2">Click to view scorecard</p>
            </div>
          `;

          popup.current
            .setLngLat(e.lngLat)
            .setHTML(popupContent)
            .addTo(map.current);
        }
      });

      map.current.on('mouseleave', 'utility-fills', () => {
        map.current.getCanvas().style.cursor = '';
        popup.current.remove();
      });

      map.current.on('click', 'utility-fills', (e) => {
        if (e.features.length > 0) {
          const utilityId = e.features[0].properties.utility_id;
          onUtilitySelect(utilityId);
          // Zoom to the clicked utility
          zoomToUtility(utilityId);
        }
      });
    }
  }, [styleLoaded, territoriesData, statesData, utilitiesData, selectedYear, onUtilitySelect, zoomToUtility]);

  // Update colors based on metric
  useEffect(() => {
    if (!map.current || !mapLoaded || !territoriesData || !utilitiesData) return;
    if (!map.current.getLayer('utility-fills')) return;

    const category = getMetricCategory(selectedMetric);
    const nationalAvg = utilitiesData.national_averages?.[selectedYear]?.[category]?.[selectedMetric];

    // Build color expression
    const colorExpression = ['match', ['get', 'utility_id']];

    territoriesData.features.forEach((feature) => {
      const utilityId = feature.properties.utility_id;
      const utility = utilitiesData.utilities?.[utilityId];
      const yearData = utility?.years?.[selectedYear];
      const value = yearData?.[category]?.[selectedMetric];

      const color = getColorForValue(value, nationalAvg, selectedMetric);
      colorExpression.push(utilityId, color);
    });

    // Default color for unmatched
    colorExpression.push('#d1d5db');

    map.current.setPaintProperty('utility-fills', 'fill-color', colorExpression);
  }, [mapLoaded, territoriesData, utilitiesData, selectedYear, selectedMetric]);

  // Update highlight for selected utility
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (!map.current.getLayer('utility-highlight')) return;

    if (selectedUtilityId) {
      map.current.setFilter('utility-highlight', ['==', 'utility_id', selectedUtilityId]);
    } else {
      map.current.setFilter('utility-highlight', ['==', 'utility_id', '']);
    }
  }, [mapLoaded, selectedUtilityId]);

  return (
    <div
      className="relative w-full h-full"
      style={{
        minHeight: '600px',
        height: '100%',
        position: 'relative'
      }}
    >
      {/* Map container with EXPLICIT dimensions */}
      <div
        ref={mapContainer}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
          minHeight: '600px',
          backgroundColor: '#e5e7eb'
        }}
      />

      {/* Custom zoom controls */}
      <div className="absolute top-4 right-4 flex flex-col space-y-2 z-10">
        {/* Zoom level indicator */}
        <div className="bg-white rounded-lg shadow-lg px-3 py-1.5 text-xs text-gray-600 text-center">
          Zoom: {currentZoom.toFixed(1)}x
        </div>

        {/* Quick zoom buttons */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <button
            onClick={zoomIn}
            className="w-full px-3 py-2 hover:bg-gray-100 transition-colors border-b border-gray-200 text-gray-700"
            title="Zoom In"
          >
            <svg className="w-5 h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </button>
          <button
            onClick={zoomOut}
            className="w-full px-3 py-2 hover:bg-gray-100 transition-colors border-b border-gray-200 text-gray-700"
            title="Zoom Out"
          >
            <svg className="w-5 h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>
          <button
            onClick={zoomToFitAll}
            className="w-full px-3 py-2 hover:bg-gray-100 transition-colors text-gray-700"
            title="Fit All (Reset View)"
          >
            <svg className="w-5 h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          </button>
        </div>

        {/* Zoom to selected utility button (only visible when utility selected) */}
        {selectedUtilityId && (
          <button
            onClick={() => zoomToUtility(selectedUtilityId)}
            className="bg-blue-600 text-white rounded-lg shadow-lg px-3 py-2 hover:bg-blue-700 transition-colors text-xs font-medium"
            title="Zoom to Selected Utility"
          >
            <svg className="w-4 h-4 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
            </svg>
            Focus
          </button>
        )}
      </div>

      {/* Map legend */}
      <div className="absolute bottom-6 left-6 bg-white rounded-lg shadow-lg p-4 z-10">
        <h4 className="font-semibold text-sm text-gray-700 mb-2">Performance vs National Average</h4>
        <div className="space-y-1">
          <div className="flex items-center text-xs">
            <div className="w-4 h-4 rounded mr-2" style={{ backgroundColor: '#10b981' }}></div>
            <span>Much Better (&gt;15%)</span>
          </div>
          <div className="flex items-center text-xs">
            <div className="w-4 h-4 rounded mr-2" style={{ backgroundColor: '#34d399' }}></div>
            <span>Better (5-15%)</span>
          </div>
          <div className="flex items-center text-xs">
            <div className="w-4 h-4 rounded mr-2" style={{ backgroundColor: '#fbbf24' }}></div>
            <span>Average (&#177;5%)</span>
          </div>
          <div className="flex items-center text-xs">
            <div className="w-4 h-4 rounded mr-2" style={{ backgroundColor: '#fb923c' }}></div>
            <span>Worse (5-15%)</span>
          </div>
          <div className="flex items-center text-xs">
            <div className="w-4 h-4 rounded mr-2" style={{ backgroundColor: '#ef4444' }}></div>
            <span>Much Worse (&gt;15%)</span>
          </div>
          <div className="flex items-center text-xs">
            <div className="w-4 h-4 rounded mr-2" style={{ backgroundColor: '#d1d5db' }}></div>
            <span>No Data</span>
          </div>
        </div>
      </div>

      {/* Zoom instructions */}
      <div className="absolute bottom-6 right-6 bg-white/90 rounded-lg shadow px-3 py-2 text-xs text-gray-500 z-10">
        <div>Scroll to zoom | Double-click to zoom in</div>
        <div>Click utility to zoom & view details</div>
      </div>

      {/* Loading territories indicator */}
      {styleLoaded && !territoriesLoaded && territoriesData && (
        <div className="absolute top-16 left-1/2 transform -translate-x-1/2 bg-white/95 px-4 py-2 rounded-lg shadow-lg border border-gray-200 z-20">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-gray-600">Loading utility territories...</span>
          </div>
        </div>
      )}
    </div>
  );
});

UtilityMap.displayName = 'UtilityMap';

export default UtilityMap;
